import { decodePolyline, haversineKm } from '@motogo/shared';
import type { Feature, FeatureCollection } from 'geojson';
import { X } from 'lucide-react';
import { useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRoutePlan } from '@/hooks/useRoutePlan';
import { useVoice } from '@/hooks/useVoice';
import { fetchRouteDetail } from '@/lib/savedRoutes';

function lineGeoJSON(coordinates: [number, number][]): FeatureCollection {
  const features: Feature[] = [
    { type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} },
  ];
  return { type: 'FeatureCollection', features };
}

/**
 * "«Chris» shared Eifel loop" — the receiving side of push-to-channel (saved routes M2).
 * Rendered by MapShell above the bottom bar on every screen. A saved-route share fetches
 * the authoritative route by id and opens its page; an inline share (unsaved sender-side)
 * carries its own geometry — View draws it straight on the map as the current route, and
 * the planner's bookmark is how the receiver saves it.
 */
const RouteSharedBanner: FC = () => {
  const navigate = useNavigate();
  const { sharedRoute, dismissSharedRoute } = useVoice();
  const { setPlannedRoute, setActiveRoute, setLoadedRoute } = useRoutePlan();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!sharedRoute) return null;

  const viewInline = (polyline: string) => {
    const latLon = decodePolyline(polyline);
    const coordinates = latLon.map(([lat, lon]) => [lon, lat] as [number, number]);
    if (coordinates.length < 2) {
      setError('The shared route is empty.');
      return;
    }
    let distanceMeters = 0;
    for (let i = 1; i < latLon.length; i++) {
      distanceMeters += haversineKm(latLon[i - 1][0], latLon[i - 1][1], latLon[i][0], latLon[i][1]) * 1000;
    }
    setPlannedRoute(lineGeoJSON(coordinates));
    setActiveRoute(null); // nothing exists server-side — this is not a saved route
    setLoadedRoute({
      kind: 'shared',
      name: sharedRoute.routeName,
      fromName: sharedRoute.fromName,
      coordinates,
      distanceMeters,
      durationSeconds: sharedRoute.durationSeconds ?? null,
    });
    dismissSharedRoute();
    navigate('/');
  };

  const view = async () => {
    if (busy) return;
    if (sharedRoute.polyline) {
      viewInline(sharedRoute.polyline);
      return;
    }
    if (!sharedRoute.routeId) return;
    setBusy(true);
    setError(null);
    try {
      const route = await fetchRouteDetail(sharedRoute.routeId);
      if (!route) {
        setError('Route is no longer available.');
        return;
      }
      const coordinates = decodePolyline(route.polyline).map(([lat, lon]) => [lon, lat] as [number, number]);
      setPlannedRoute(lineGeoJSON(coordinates));
      setActiveRoute({ id: route.id, name: route.name, description: route.description, visibility: route.visibility });
      setLoadedRoute(null);
      dismissSharedRoute();
      navigate(`/routes/${route.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the route');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mg-share-banner" role="status">
      <div className="mg-share-banner-text">
        <strong>{sharedRoute.fromName}</strong> shared <em>{sharedRoute.routeName}</em>
        {error && <span className="mg-share-banner-error"> — {error}</span>}
      </div>
      <button className="mg-btn mg-btn-primary mg-share-banner-btn" onClick={() => void view()} disabled={busy}>
        {busy ? '…' : 'View'}
      </button>
      <button className="mg-icon-btn" onClick={dismissSharedRoute} aria-label="Dismiss" title="Dismiss">
        <X size={20} />
      </button>
    </div>
  );
};

export default RouteSharedBanner;
